import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { IntakeData } from '@/components/pricing/conciergeDraft';

interface SubmitApplicationParams {
  intakeData: IntakeData;
  packageId: string;
  packageName: string;
}

export function useConciergeApplications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: myApplications, isLoading } = useQuery({
    queryKey: ['concierge-applications', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('concierge_applications')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const submitApplication = useMutation({
    mutationFn: async ({ intakeData, packageId, packageName }: SubmitApplicationParams) => {
      if (!user) throw new Error('Not authenticated');

      // Check if user is Plus member for priority
      const { data: membership } = await supabase
        .from('user_memberships')
        .select('tier')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      const isPriority = membership?.tier === 'plus' || membership?.tier === 'premium' || membership?.tier === 'elite';

      const { data, error } = await supabase
        .from('concierge_applications')
        .insert({
          user_id: user.id,
          package_id: packageId,
          package_name: packageName,
          priority_level: isPriority ? 'priority' : 'standard',
          dining_atmosphere: intakeData.diningAtmosphere,
          profession: intakeData.profession,
          income_range: intakeData.incomeRange || null,
          partner_qualities: intakeData.partnerQualities,
          availability: intakeData.availability,
          travel_willingness: intakeData.travelWillingness,
          communication_pref: intakeData.communicationPref,
        })
        .select()
        .single();

      if (error) throw error;

      // Mark user as pending concierge on their profile
      await supabase
        .from('profiles')
        .update({ concierge_status: 'pending' })
        .eq('id', user.id);

      // Trigger AI scoring via edge function
      supabase.functions.invoke('ai-concierge-score', {
        body: { application_id: data.id },
      }).catch(console.error);

      // Send concierge application received confirmation email
      const { data: profileData } = await supabase
        .from('profiles')
        .select('first_name, email')
        .eq('id', user.id)
        .single();

      if (profileData?.email) {
        supabase.functions.invoke('send-transactional-email', {
          body: {
            templateName: 'concierge-received',
            recipientEmail: profileData.email,
            idempotencyKey: `concierge-received-${data.id}`,
            templateData: {
              firstName: profileData.first_name || undefined,
            },
          },
        }).catch(console.error);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['concierge-applications'] });
    },
  });

  return { myApplications, isLoading, submitApplication };
}
